// tenant/MenuPages/TenantBillingPage.tsx
import React, { useEffect, useState, CSSProperties } from 'react';
import { useHistory } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useTenantUserInfo } from '../../context/userInfoProvider';
import { submitStripeBilling } from '../../components/TenantApp/createStripeCustomer';
import type { StripeBillingInput } from '../../components/TenantApp/createStripeCustomer';

// Load Stripe outside component to avoid recreating on each render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

// ---- Styles ----------------------------------------------------------------

const pageContainer: CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background: 'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: CSSProperties = {
  marginBottom: 16,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const titleStyle: CSSProperties = { fontSize: 22, fontWeight: 600 };
const subtitleStyle: CSSProperties = { fontSize: 14, opacity: 0.8, marginTop: 4 };
const backLink: CSSProperties = { fontSize: 13, opacity: 0.85, textDecoration: 'underline', cursor: 'pointer' };

const sectionCard: CSSProperties = {
  backgroundColor: 'rgba(23, 8, 36, 0.9)',
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.05)',
};

const labelStyle: CSSProperties = { fontSize: 13, fontWeight: 500, marginBottom: 4 };

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #4826A0',
  fontSize: 14,
  backgroundColor: 'rgba(10, 4, 18, 0.9)',
  color: '#F5F5FA',
  boxSizing: 'border-box',
};

// Stripe CardElement needs its own container styled to match inputs
const cardElementContainer: CSSProperties = {
  ...inputStyle,
  padding: '10px',
  marginTop: 4,
};

const errorTextStyle: CSSProperties = { fontSize: 12, color: '#FFB3B3', marginTop: 4 };
const hintTextStyle: CSSProperties = { fontSize: 12, color: '#D0C9E9', marginTop: 4 };

const rowLayout: CSSProperties = {
  display: 'flex',
  gap: 12,
  marginTop: 12,
  flexWrap: 'wrap',
};

const primaryButton: CSSProperties = {
  marginTop: 20,
  width: '100%',
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'center',
};

const disabledButton: CSSProperties = { ...primaryButton, opacity: 0.6, cursor: 'not-allowed' };

const statusText: CSSProperties = { fontSize: 12, marginTop: 10 };
const successTextStyle: CSSProperties = { ...statusText, color: '#C8F7C5' };

const debugCardStyle: CSSProperties = {
  marginTop: 16,
  padding: 12,
  borderRadius: 8,
  backgroundColor: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.08)',
};

const debugPreStyle: CSSProperties = {
  marginTop: 8,
  fontSize: 12,
  color: '#D0C9E9',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

// Stripe CardElement appearance options
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: '#F5F5FA',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#8B7AA8' },
      iconColor: '#9B6DE0',
    },
    invalid: {
      color: '#FFB3B3',
      iconColor: '#FFB3B3',
    },
  },
};

// ---- Inner form (must be a child of <Elements>) ----------------------------

const BillingForm: React.FC = () => {
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();

  const { loading, name, email, addressLine1, addressLine2, city, state, zip } =
    useTenantUserInfo();

  const [localLoading, setLocalLoading] = useState(true);

  // Billing address state — NO card fields
  const [billingName, setBillingName] = useState('');
  const [billingAddressLine1, setBillingAddressLine1] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');

  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testingStripe, setTestingStripe] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [debugResponse, setDebugResponse] = useState<any>(null);

  useEffect(() => {
    setBillingName(name || '');
    setBillingAddressLine1(addressLine1 || '');
    setBillingAddressLine2(addressLine2 || '');
    setBillingCity(city || '');
    setBillingState(state || '');
    setBillingZip(zip || '');
    setLocalLoading(false);
  }, [name, addressLine1, addressLine2, city, state, zip]);

  const isBusy = loading || localLoading || saving || testingStripe;

  const validateAddressFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!billingAddressLine1.trim()) errors.billingAddressLine1 = 'Billing address is required.';
    if (!billingCity.trim()) errors.billingCity = 'City is required.';
    if (!billingState.trim()) errors.billingState = 'State is required.';
    if (!/^\d{5}$/.test(billingZip)) errors.billingZip = 'ZIP must be 5 digits.';

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setFormError('Please fix the highlighted fields.');
      return false;
    }

    setFormError(null);
    return true;
  };

  const getBillingInput = (): StripeBillingInput => ({
    email: email || '',
    billingName,
    billingAddressLine1,
    billingAddressLine2,
    billingCity,
    billingState,
    billingZip,
    // ✅ No card fields — Stripe Elements handles card data
  });

  const runStripeFlow = async (mode: 'save' | 'test') => {
    if (!validateAddressFields()) return;

    if (!email) {
      setFormError('Missing tenant email.');
      return;
    }

    if (!stripe || !elements) {
      setFormError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const setter = mode === 'save' ? setSaving : setTestingStripe;

    try {
      setter(true);
      setFormError(null);
      setStatus(mode === 'save' ? 'Creating Stripe customer…' : 'Testing Stripe customer creation…');
      setDebugResponse(null);

      // Card data goes directly to Stripe — never touches our code
      const result = await submitStripeBilling(stripe, elements, getBillingInput());

      setDebugResponse(result);
      setStatus(
        `${mode === 'save' ? 'Billing information saved' : 'Stripe test succeeded'}.\nCustomer ID: ${result.stripeCustomerID}\nStatus: ${result.stripeStatus}`
      );
    } catch (err: any) {
      setDebugResponse({ error: err?.message || 'Unknown error' });
      setStatus(`${mode === 'save' ? 'Save' : 'Test'} failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setter(false);
    }
  };

  const isValidInput =
    !localLoading &&
    cardComplete &&
    billingAddressLine1.trim().length > 0 &&
    billingCity.trim().length > 0 &&
    billingState.trim().length > 0 &&
    billingZip.trim().length === 5;

  if (loading && localLoading) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <p>Loading billing page…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerRow}>
          <div>
            <h1 style={titleStyle}>Billing</h1>
            <p style={subtitleStyle}>
              Update your payment method and billing address for Bins.
            </p>
          </div>
          <div style={backLink} onClick={() => history.push('/tenant/menu')}>
            Back to menu
          </div>
        </div>

        {/* ---- Card section ---- */}
        <section style={sectionCard}>
          <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
            Credit card
          </h3>
          {/*
            ✅ Stripe-hosted iframe — card number, CVV, and expiry are entered here.
            This data goes directly to Stripe; our JavaScript never sees the raw values.
          */}
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Card details</label>
            <div style={cardElementContainer}>
              <CardElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={e => {
                  setCardComplete(e.complete);
                  setCardError(e.error ? e.error.message : null);
                }}
              />
            </div>
            {cardError && <div style={errorTextStyle}>{cardError}</div>}
          </div>
        </section>

        {/* ---- Billing address section ---- */}
        <section style={{ ...sectionCard, marginTop: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
            Billing address
          </h3>

          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Name on card</label>
            <input
              type="text"
              value={billingName}
              onChange={e => setBillingName(e.target.value)}
              style={inputStyle}
              disabled={isBusy}
            />
          </div>

          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>Address line 1</label>
            <input
              type="text"
              value={billingAddressLine1}
              onChange={e => {
                setBillingAddressLine1(e.target.value);
                setFieldErrors(prev => ({ ...prev, billingAddressLine1: '' }));
              }}
              style={inputStyle}
              disabled={isBusy}
            />
            {fieldErrors.billingAddressLine1 && (
              <div style={errorTextStyle}>{fieldErrors.billingAddressLine1}</div>
            )}
          </div>

          <div style={{ marginTop: 8 }}>
            <label style={labelStyle}>
              Address line 2 <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text"
              value={billingAddressLine2}
              onChange={e => setBillingAddressLine2(e.target.value)}
              style={inputStyle}
              disabled={isBusy}
            />
          </div>

          <div style={rowLayout}>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={labelStyle}>City</label>
              <input
                type="text"
                value={billingCity}
                onChange={e => {
                  setBillingCity(e.target.value);
                  setFieldErrors(prev => ({ ...prev, billingCity: '' }));
                }}
                style={inputStyle}
                disabled={isBusy}
              />
              {fieldErrors.billingCity && (
                <div style={errorTextStyle}>{fieldErrors.billingCity}</div>
              )}
            </div>
            <div style={{ width: 80, minWidth: 80 }}>
              <label style={labelStyle}>State</label>
              <input
                type="text"
                value={billingState}
                onChange={e => {
                  setBillingState(e.target.value.toUpperCase());
                  setFieldErrors(prev => ({ ...prev, billingState: '' }));
                }}
                style={inputStyle}
                maxLength={2}
                disabled={isBusy}
              />
              {fieldErrors.billingState && (
                <div style={errorTextStyle}>{fieldErrors.billingState}</div>
              )}
            </div>
            <div style={{ width: 100, minWidth: 90 }}>
              <label style={labelStyle}>ZIP</label>
              <input
                type="text"
                value={billingZip}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setBillingZip(v);
                  setFieldErrors(prev => ({ ...prev, billingZip: '' }));
                }}
                style={inputStyle}
                disabled={isBusy}
              />
              {fieldErrors.billingZip && (
                <div style={errorTextStyle}>{fieldErrors.billingZip}</div>
              )}
            </div>
          </div>

          <p style={hintTextStyle}>
            By saving your billing details, you agree to Bins&apos; Terms &amp; Conditions and
            Privacy Policy.
          </p>
        </section>

        {formError && <div style={errorTextStyle}>{formError}</div>}
        {status && (
          <div
            style={
              status.toLowerCase().includes('failed') || status.toLowerCase().includes('problem')
                ? statusText
                : successTextStyle
            }
          >
            {status}
          </div>
        )}

        <button
          type="button"
          onClick={() => runStripeFlow('save')}
          style={!isValidInput || isBusy ? disabledButton : primaryButton}
          disabled={!isValidInput || isBusy}
        >
          {saving ? 'Saving…' : 'Save billing information'}
        </button>

        {debugResponse && (
          <div style={debugCardStyle}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Temporary debug response</div>
            <pre style={debugPreStyle}>{JSON.stringify(debugResponse, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Outer component wraps form in <Elements> provider -------------------

const TenantBillingPage: React.FC = () => (
  <Elements stripe={stripePromise}>
    <BillingForm />
  </Elements>
);

export default TenantBillingPage;